import './CourseDiscovery.scss'

import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { Container, Row, Col } from 'react-bootstrap'

import {
  saveUserEvent,
  EVENT_CATEGORY_COURSE,
  EVENT_ACTION_PAGEVIEW,
  EVENT_VALUE_ALL
} from 'components/common/api/UserEvents'
import CourseContext from 'components/common/contexts/CourseContext'
import CourseCategoryFilter from '../CourseCategoryFilter'
import ScrollingCards from '../../common/navigation/ScrollingCards'
import Loading from '../../../templates/Loading'
import {
  ROUTE_ASSESSMENT_PROGRAM,
  ROUTE_COURSES_ALL
} from '../../common/helpers/Paths'
import {
  getCourseCategories,
  getRecommendedCourses,
  isFeaturedCategory,
  isFreeCategory,
  tryGetCourseField,
  COURSE_FIELD_COURSE_LENGTH
} from '../../common/api/Courses'
import { app } from '../../../config'
import { navigateTo } from 'components/common/navigation'
import { useHistory } from 'react-router-dom'

const featuredCategoryId = parseInt(app.wp_api.featured_category_id, 10)

/**
 * Course Discovery component. Used to display the Journeys landing page.
 * @param {Object} props Incoming props
 * @param {Function} props.setReturnPath Function to set the return path
 * @param {Function} props.setHeaderTitle Function to set the header title
 */
export default function CourseDiscovery({
  setReturnPath,
  setHeaderTitle,
  cache
}) {
  const history = useHistory()
  const { courses, progress } = useContext(CourseContext)

  const [masterClasses, setMasterClasses] = useState([])
  const [coursesFiltered, setCoursesFiltered] = useState()
  const [recommendedCourses, setRecommendedCourses] = useState([])
  const [featuredCourses, setFeaturedCourses] = useState([])
  const [coursesInProgress, setCoursesInProgress] = useState([])
  const [activeFilterIndex, setActiveFilterIndex] = useState(0)
  const [showSpecificGroups, setShowSpecificGroups] = useState(true)
  const [newCoursesFiltered, setNewCoursesFiltered] = useState([])
  const [allCategories, setAllCategories] = useState([])
  const [categories, setCategories] = useState([])

  useEffect(() => {
    // Filter all courses by course length
    const filteredArray =
      courses?.length > 0 &&
      courses.filter(
        course => tryGetCourseField(course, COURSE_FIELD_COURSE_LENGTH) > 1
      )

    filteredArray && setMasterClasses(filteredArray)
  }, [courses])

  const setActiveFilter = index => {
    // Return early if we don't have any courses
    if (!courses?.length) return

    setActiveFilterIndex(index)
  }

  // Get Recommended Courses
  const getRecommendedCoursesAsync = useCallback(async () => {
    // Return early if we already have recommended courses
    if (recommendedCourses.length) return

    // Use today's date to determine if we should refresh the cache
    const today = new Date().getDate()

    // Get recommended courses
    const recommended = await cache.tryGetAsyncResult(
      `recommended-courses-${today}`,
      getRecommendedCourses,
      this
    )

    setRecommendedCourses(recommended)
  }, [cache, recommendedCourses])

  // Get Course Categories
  const getCourseCategoriesAsync = useCallback(async () => {
    // Return early if we already have categories
    if (allCategories.length) return

    // Pull course info from cache
    let courseCategories = await cache.tryGetAsyncResult(
      'course-categories',
      getCourseCategories,
      this
    )

    setAllCategories(courseCategories)
  }, [cache, allCategories])

  /**
   * Handler function for when a filter is selected. This function will update the newCoursesFiltered state with the filtered courses.
   * @param {Object} parentCategory The category that was selected
   */
  const handleFilterChange = useCallback(
    parentCategory => {
      // If the parent category is the "All" category, then we want to show all courses
      if (parentCategory.id === 0) {
        setNewCoursesFiltered([])
        return
      }

      // Now that we have the matching category, let's get the ID and then find all subcategories with that parent
      const matchingSubcategories = allCategories.filter(
        category => category.parent === parentCategory.id
      )

      // Now that we have all the subcategory IDs, let's iterate through the category array
      // and the courses and find any that match
      const filteredCourses = matchingSubcategories.map(subcategory => ({
        title: subcategory.name,
        courses: courses.filter(course =>
          course.ld_course_category.includes(subcategory.id)
        )
      }))

      setNewCoursesFiltered(filteredCourses)

      return filteredCourses.reduce(
        (totalCourses, category) => [...totalCourses, ...category.courses],
        []
      )
    },
    [allCategories, courses]
  )

  // Active filter effect
  useEffect(() => {
    setShowSpecificGroups(activeFilterIndex === 0)
  }, [activeFilterIndex])

  // Main effect
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Update header and return path
    setReturnPath(null)
    setHeaderTitle(null)

    // Return early if we don't have any course content yet
    if (!courses?.length) return

    // Sort the courses by ID descending, as a more performant proxy for created_date
    const sorted = courses.sort((a, b) => b.id - a.id)

    // Set the initial filter value
    setCoursesFiltered(sorted)

    // Get courses in progress or an empty array if there are none
    const inProgress = !progress?.length
      ? []
      : progress
          .map(match =>
            // Find all courses in progress with at least one topic completed,
            // but don't include courses that are already completed
            courses.find(
              course =>
                match.course === course.id &&
                match.steps_completed > 0 &&
                match.steps_completed < match.steps_total
            )
          )
          .filter(course => course) // Remove any undefined courses

    // Get featured courses, or an empty array if there are none
    const featured = !courses?.length
      ? []
      : courses.filter(course =>
          course.ld_course_category.includes(featuredCategoryId)
        )

    setFeaturedCourses(featured)
    setCoursesInProgress(inProgress)

    // Get recommended courses
    getRecommendedCoursesAsync()

    // Get course categories
    getCourseCategoriesAsync()

    // Log access
    saveUserEvent(EVENT_CATEGORY_COURSE, EVENT_ACTION_PAGEVIEW, EVENT_VALUE_ALL)
  }, [
    courses,
    progress,
    setHeaderTitle,
    setReturnPath,
    getCourseCategoriesAsync,
    getRecommendedCoursesAsync
  ])

  // Memoized categories
  useMemo(() => {
    // Return early if we don't have any courses or categories yet
    if (!allCategories.length || !courses?.length) return

    // Filter out child categories and the free and featured categories
    const journeyCategories = allCategories.filter(
      category =>
        category.parent === 0 &&
        !isFreeCategory(category.id) &&
        !isFeaturedCategory(category.id)
    )

    // Create an object with the category name as the key and an array of courses as the value
    let categoriesObject = journeyCategories?.reduce((newObject, category) => {
      const { name, id } = category
      newObject[name] = courses.filter(
        course => course && course.ld_course_category.includes(id)
      )
      return newObject
    }, {})

    // Add an "All" category to the beginning of the object
    categoriesObject = {
      All: courses,
      ...categoriesObject
    }

    // Set the categories
    setCategories(categoriesObject)
  }, [courses, allCategories])

  return (
    <Container className='course-discovery-container pb-5'>
      <h1 className='title mb-4'>Your well-being starts here</h1>
      <p className='mb-4'>
        Explore on your own or use our{' '}
        <span
          className='green text-decoration-underline'
          onClick={_ => navigateTo(ROUTE_ASSESSMENT_PROGRAM, history)}
        >
          guided assessment
        </span>{' '}
        to find the right path for you
      </p>
      <Row>
        <Col xs={12}>
          <CourseCategoryFilter
            allCategories={allCategories}
            categories={categories}
            handleFilterChange={handleFilterChange}
            filteredCourses={coursesFiltered}
            activeFilterIndex={activeFilterIndex}
            setActiveFilter={setActiveFilter}
          />
        </Col>
      </Row>
      <Row>
        {/* Show spinner if courses aren't loaded yet */}
        <Loading isLoaded={!courses?.length} loadingClass='h-100 pt-5'>
          {/* Show all subcategories that match the parent category and the courses within */}
          {!!newCoursesFiltered.length &&
            newCoursesFiltered.map(subcategory => (
              <Col xs={12} key={subcategory.title}>
                <h3 className='mb-0'>{subcategory.title}</h3>
                <ScrollingCards
                  courses={subcategory.courses}
                  returnPath={ROUTE_COURSES_ALL}
                />
              </Col>
            ))}
          {showSpecificGroups && (
            <>
              {/* Only show courses in progress if user has courses in progress */}
              {!!coursesInProgress?.length && (
                <Col xs={12}>
                  <h3 className='mb-0'>Continue Watching</h3>
                  <ScrollingCards
                    courses={coursesInProgress}
                    emptyText='Your Sessions in progress will show up here. Get started with one of our Featured Courses!'
                    returnPath={ROUTE_COURSES_ALL}
                  />
                </Col>
              )}
              {/* Only show master class courses */}
              {!!masterClasses?.length && (
                <Col xs={12} className='master-classes'>
                  <div className='classes-container bg-primary position-relative text-white pt-4'>
                    <small>Take your well-being deeper with our</small>
                    <h3 className='mb-0 text-white'>Courses</h3>
                    <ScrollingCards
                      courses={masterClasses}
                      returnPath={ROUTE_COURSES_ALL}
                    />
                  </div>
                </Col>
              )}
              {/* Only show recommendations if we have some to show */}
              {!!recommendedCourses?.length && (
                <Col xs={12}>
                  <h3 className='mb-0'>Recommended for You</h3>
                  <ScrollingCards
                    courses={recommendedCourses}
                    returnPath={ROUTE_COURSES_ALL}
                  />
                </Col>
              )}
              <Col xs={12}>
                <h3 className='mb-0'>New and Featured</h3>
                <ScrollingCards
                  courses={featuredCourses}
                  returnPath={ROUTE_COURSES_ALL}
                />
              </Col>

              {/*Filter out the "All" category*/}
              {categories &&
                Object.keys(categories)?.map(
                  title =>
                    title !== 'All' &&
                    categories[title]?.length > 0 && (
                      <Col xs={12} key={title}>
                        <h3 className='mb-0'>{title}</h3>
                        <ScrollingCards
                          courses={categories[title]}
                          returnPath={ROUTE_COURSES_ALL}
                        />
                      </Col>
                    )
                )}
            </>
          )}
        </Loading>
      </Row>
    </Container>
  )
}
